import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { FormControlSizes } from '../types';

type ToggleGroupIndicator = {
  x: number;
  y: number;
  width: number;
  height: number;
  visible: boolean;
};

type UseToggleGroupIndicatorParams = {
  useSlideAnimation: boolean;
  value: string | undefined;
  options: Array<unknown>;
  orientation: 'horizontal' | 'vertical';
  size: FormControlSizes;
  rootRef: RefObject<HTMLDivElement | null>;
  itemRefs: RefObject<Record<string, HTMLDivElement | null>>;
};

const hiddenIndicator: ToggleGroupIndicator = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  visible: false,
};

export const useToggleGroupIndicator = ({
  useSlideAnimation,
  value,
  options,
  orientation,
  size,
  rootRef,
  itemRefs,
}: UseToggleGroupIndicatorParams): ToggleGroupIndicator => {
  const [indicator, setIndicator] = useState<ToggleGroupIndicator>(hiddenIndicator);
  const updateIndicatorRef = useRef<(() => void) | null>(null);

  const updateIndicator = useCallback(() => {
    if (!useSlideAnimation) {
      return;
    }
    if (value == null) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }

    const root = rootRef.current;
    const items = itemRefs.current;
    const item = items == null ? null : items[String(value)];
    if (root == null || item == null) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }

    // offset* (layout space, relative to offsetParent) is unaffected by ancestor transforms like Modal's enter animation, unlike getBoundingClientRect().
    setIndicator({
      x: item.offsetLeft,
      y: item.offsetTop,
      width: item.offsetWidth,
      height: item.offsetHeight,
      visible: true,
    });
  }, [itemRefs, rootRef, useSlideAnimation, value]);

  useEffect(() => {
    updateIndicatorRef.current = updateIndicator;
  }, [updateIndicator]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [options, orientation, size, updateIndicator, useSlideAnimation]);

  useEffect(() => {
    if (!useSlideAnimation) {
      return;
    }

    const root = rootRef.current;
    const handleResize = () => updateIndicatorRef.current?.();

    if (root != null && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(root);
      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [rootRef, useSlideAnimation]);

  return indicator;
};
